import * as React from "react";
import { Grid, Typography } from "@mui/material";
import NewSlider from "../Slider";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  getChatHistory,
  modifyChatHistory,
} from "../../redux/slices/userSlice";
import SettingRow from "../SettingRow";

interface ChatHistoryProps {
  value?: number;
  onChange?: (v: number) => void;
}

/**
 * ChatHistory:
 * - If `value` and `onChange` props are provided, the component acts as a controlled UI
 *   and calls onChange when the slider moves (does NOT dispatch to Redux).
 * - If props are not provided, it falls back to reading + writing from Redux as before.
 */
export default function ChatHistory(props: ChatHistoryProps): JSX.Element {
  const { value: controlledValue, onChange } = props;
  const dispatch = useAppDispatch();
  const historyValueFromStore = useAppSelector(getChatHistory);

  // When uncontrolled (no props), keep a local value that reflects Redux (so slider is responsive)
  const [localValue, setLocalValue] = React.useState<number>(
    typeof historyValueFromStore === "number" ? historyValueFromStore : 0
  );

  React.useEffect(() => {
    if (typeof historyValueFromStore === "number") {
      setLocalValue(historyValueFromStore);
    }
  }, [historyValueFromStore]);

  React.useEffect(() => {
    // If the store contains 0 (explicit), set a sensible default of 0 as before
    if (historyValueFromStore === 0) {
      dispatch(modifyChatHistory(0));
    }
  }, [historyValueFromStore, dispatch]);

  const valueToUse =
    typeof controlledValue === "number" ? controlledValue : localValue;

  const handleChange = (v: number) => {
    if (typeof onChange === "function") {
      // Controlled mode: inform parent (SettingsModal)
      onChange(v);
    } else {
      // Uncontrolled mode: update local + redux as before
      setLocalValue(v);
      dispatch(modifyChatHistory(v));
    }
  };

  return (
    <SettingRow
      title="Refer Chat History"
      tooltip="Set the number of previous chat messages to include for context. A higher number provides more context but may increase response time."
    >
      <Grid container alignItems="center" spacing={1}>
        {/* Slider column: fixed responsive width so all sliders match */}
        <Grid
          item
          sx={{
            flexBasis: { xs: "200px", sm: "280px" },
            flexGrow: 0,
            flexShrink: 0,
          }}
        >
          <NewSlider
            value={valueToUse}
            setValue={(value: number) => handleChange(value)}
            min={0}
            max={50}
            step={1}
            default_value={0}
            testid="chat-history-slider"
          />
        </Grid>

        {/* Numeric value column: fixed min width to avoid layout shifts */}
        <Grid item>
          {/* <Typography sx={{ fontSize: "12px", minWidth: "40px", textAlign: "right" }}> */}
          <Typography sx={{ minWidth: "40px", textAlign: "right" }}>
            {valueToUse}
          </Typography>
        </Grid>
      </Grid>
    </SettingRow>
  );
}
